import { validateFileSize } from 'app/utils/file'
import { useContext, useState, useEffect } from 'react'
import { AuthenticationContext } from '../Contexts/AuthenticationProvider'

type Options = {
  limitInMb?: number
}

type GenericFileUploadInput = {
  file: File
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

type GenericUploadResponse = {}

export const useFileUpload = <
  F extends GenericFileUploadInput,
  R extends GenericUploadResponse
>(
  endpointUrl: string
) => {
  const authCtx = useContext(AuthenticationContext)
  const authToken = authCtx?.authToken || ''

  const [uploadIsLoading, setUploadIsLoading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [uploadSuccess, setUploadSuccess] = useState<R | null>()
  const [requestBody, setRequestBody] = useState<FormData | null>()

  useEffect(() => {
    if (requestBody) {
      // console.log('start upload')
      setUploadIsLoading(true)

      fetch(endpointUrl, {
        method: 'POST',
        headers: {
          Authorization: authToken,
        },
        body: requestBody,
      })
        .then(
          response => response.json() // if the response is a JSON object
        )
        .then((res: R) => {
          setUploadSuccess(res)
        })
        .catch(error => {
          // console.log('failed upload')
          setUploadError((error && error.message) || 'Upload failed')
        })
        .finally(() => {
          // console.log('completed upload')
          setUploadIsLoading(false)
        })
    }
  }, [requestBody])

  const uploadFile = ({ file, ...additionalParams }: F, options?: Options) => {
    if (!file) {
      setUploadError('Nessun file caricato')
      return
    }

    const limitInMb = options && options.limitInMb

    const { isValid: isFileSizeValid, error: fileSizeError } = validateFileSize(
      file.size,
      limitInMb
    )

    if (!isFileSizeValid && fileSizeError) {
      setUploadError(fileSizeError)
    }

    // console.log('preparing new body request')
    const body = new FormData()
    body.append('file', file)
    Object.keys(additionalParams).forEach(paramName => {
      body.append(paramName, additionalParams[paramName])
    })

    setRequestBody(body)
  }

  return {
    uploadFile,
    uploadSuccess,
    uploadIsLoading,
    uploadError,
  }
}
